import { faCheck as faSolidCheck } from '@fortawesome/pro-solid-svg-icons';
import MuiCheckbox, {
  CheckboxProps as MuiCheckboxProps,
} from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

import { Box } from './Box';
import { Icon } from './Icon';
import { TypographyProps } from './Typography';

export interface CheckboxProps extends MuiCheckboxProps {
  label?: string | React.ReactNode;
  useSwitch?: boolean;
  expandedClickarea?: boolean;
  variant?: 'light' | 'regular';
  typographyVariant?: TypographyProps['variant'];
}

/**
 * This component is a thin wrapper around MuiCheckbox.
 * 1. Adds a interactive text label via the label prop
 * 2. Allows for using a switch instead of checkbox via useSwitch
 */
export const Checkbox: React.FC<CheckboxProps> = ({ ...props }) => {
  const {
    label,
    useSwitch,
    size,
    sx,
    variant = 'regular',
    typographyVariant = 'subtitle2',
    expandedClickarea = true,
    ...otherProps
  } = props;

  // Extract only the props that are common to both Switch and Checkbox
  const commonProps: Pick<
    CheckboxProps,
    'checked' | 'onChange' | 'disabled' | 'value' | 'name' | 'required'
  > = {
    checked: otherProps.checked,
    onChange: otherProps.onChange,
    disabled: otherProps.disabled,
    value: otherProps.value,
    name: otherProps.name,
    required: otherProps.required,
  };
  return (
    <FormControlLabel
      disabled={otherProps.disabled}
      label={label}
      sx={sx}
      slotProps={{
        typography: { variant: typographyVariant },
      }}
      control={
        <Box
          sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
        >
          {/* Overlay to expand click area beyond the bounds of the checkbox without affecting positioning */}
          {expandedClickarea && !otherProps.disabled && (
            <Box
              sx={{
                position: 'absolute',
                width: 'calc(100% + 20px)',
                height: 'calc(100% + 20px)',
                margin: '-10px -10px -10px -10px',
              }}
            ></Box>
          )}
          {useSwitch === true ? (
            <Switch
              sx={[
                {
                  padding: '6px',
                  margin: '0px',
                  '& .MuiSwitch-track': { borderRadius: '15px' },
                  '& .Mui-checked+.MuiSwitch-track': { opacity: 1 },
                  '& .MuiSwitch-thumb': { bgcolor: 'white' },
                },
                size === 'small' && {
                  padding: '2px',
                  width: '34px',
                  '& .MuiSwitch-thumb': {
                    width: '13px',
                    height: '13px',
                    marginTop: '1.5px',
                    marginLeft: '1px',
                  },
                  '& .MuiSwitch-track': { borderRadius: '15px' },
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    transform: 'translateX(11px)',
                  },
                },
              ]}
              size={size as 'small' | 'medium'}
              {...commonProps}
            />
          ) : (
            <MuiCheckbox
              size={size}
              {...(variant === 'light' && {
                icon: (
                  <Box
                    sx={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      border: '1px solid',
                      borderColor: 'neutral.300',
                      m: '1px',
                    }}
                  ></Box>
                ),
                checkedIcon: (
                  <Box
                    sx={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '4px',
                      backgroundColor: 'primary.main',
                      border: '1px solid',
                      borderColor: 'primary.main',
                      m: '1px',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon
                      icon={faSolidCheck}
                      size="tiny"
                      color="inherit"
                    ></Icon>
                  </Box>
                ),
              })}
              {...otherProps}
            />
          )}
        </Box>
      }
    />
  );
};
